import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import i18n from 'i18next';

import { AppButton } from '@components/app/AppButton/AppButton';
import { SelectDropdown } from '@components/app/SelectDropdown/SelectDropdown';
import { Loader } from '@components/shared/Loader/Loader';
import { useInjection } from '@context/inversify-context-provider';
import { AuthHelper } from '@helpers/authHelper';
import { setMomentLocale } from '@helpers/date-locales.helper';
import { getTenantCountryCode, getTenantDefaultLanguage, getTenantLanguageList } from '@helpers/tenant.helper';
import { TYPES } from '@libs/InversifyType';
import { RootState } from '@store/rootReducer';
import { snackbarSlice } from '@store/slices/snackbar';
import { useQuery } from '@tanstack/react-query';

import { AamBackendApi } from '../../libs/aamBackendApi';

export const AppLoginPage = () => {
  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const b2c = useSelector((state: RootState) => state.featureConfiguration.b2c);
  const userId = useSelector((state: RootState) => state.authApp.userId);
  const { tenantId } = useSelector((state: RootState) => state.tenant);
  const appSettings = useSelector((state: RootState) => state.featureConfiguration.appSettings);
  const authHelper = useInjection<AuthHelper>(TYPES.AuthHelper);
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation(['candidate', 'candidate_recruiter']);
  const sessionExpiredApp = useSelector((state: RootState) => state.authApp.sessionExpired);

  const [selectedLanguage, setSelectedLanguage] = useState<any>(getTenantDefaultLanguage(tenantId));
  const { search } = useLocation();
  const candidateId = new URLSearchParams(search).get('candidateId');

  const changeLanguageHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const lang = e.target.value;
    setSelectedLanguage(lang);
    setMomentLocale(tenantId, lang);
    if (i18n?.services?.languageUtils) {
      i18n.changeLanguage(lang);
    }
  };

  useEffect(() => {
    if (i18n?.services?.languageUtils) {
      const lang = getTenantDefaultLanguage(tenantId);
      i18n.changeLanguage(lang);
      setSelectedLanguage(lang);
      setMomentLocale(tenantId, lang);
    }
  }, [tenantId]);

  const b2cSignIn = async () => {
    const signInUrl = await authHelper.getAuthorizeUrlSignIn(b2c.signIn, history.location);
    authHelper.b2cNavigation(
      b2c.signIn,
      signInUrl + '&ui_locales=' + selectedLanguage + '&country=' + getTenantCountryCode(tenantId),
      history,
      appSettings.useExternalBrowser,
    );
  };
  const b2cSignUp = async (email?: string) => {
    history.push('/app/preregister', { email });
  };
  if (sessionExpiredApp || new URLSearchParams(window.location.search).get('sessionExpired')) {
    dispatch(
      snackbarSlice.actions.showError({
        message: t('candidate_recruiter:GENERAL.GENERIC.sessionExpired'),
      }),
    );
  }

  const query = useQuery({
    queryKey: ['checkUser', tenantId, candidateId],
    queryFn: () => aamBackendApi.checkUser(tenantId, candidateId),
    refetchOnReconnect: false,
    enabled: !!candidateId,
  });

  const isFetching = query.isFetching ?? true;

  useEffect(() => {
    const data = query.data as
      | { email: string; b2cIdExists: boolean; legalRequiredConsentAccepted: boolean }
      | undefined;
    if (candidateId && candidateId !== 'null' && candidateId !== 'undefined' && data) {
      if (data.b2cIdExists) {
        b2cSignIn();
      } else {
        b2cSignUp(data.email);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, query.data]);

  if (userId) {
    history.push('/app');
    return <></>;
  }

  if (isFetching) {
    return <Loader />;
  }

  return (
    <>
      <div className="loginSignin">
        <div className="loginSignin__select-language">
          <div className="header__select-language-box">
            <SelectDropdown
              title={t('Select Language')}
              options={getTenantLanguageList(tenantId).map((x) => {
                return { name: x, value: x };
              })}
              onChange={changeLanguageHandler}
              value={selectedLanguage}
              name="selectedLanguage"
            />
          </div>
        </div>
        <div className="loginSignin__main-content">
          <div className="adeccoLogo">
            <svg
              className="svgItself"
              width="210"
              height="53"
              viewBox="0 0 210 53"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M74.7666 0C72.5304 0 71.0173 1.61437 71.0173 4.011V18.4669C69.1156 16.6819 66.2876 15.1935 62.7905 15.1935C52.6853 15.1935 47.3946 24.5726 47.3946 33.8284C47.3946 43.0815 52.6853 52.4554 62.7905 52.4554C66.4575 52.4554 69.2495 51.387 71.1665 49.3185C71.4959 51.2033 72.8212 52.4554 74.7666 52.4554C77.0027 52.4554 78.5107 50.8436 78.5107 48.4391V4.011C78.5107 1.61437 77.0027 0 74.7666 0ZM54.8777 33.8284C54.8777 28.3868 57.0701 22.0474 63.2434 22.0474C69.4193 22.0474 71.5963 28.3868 71.5963 33.8284C71.5963 39.2674 69.4193 45.5963 63.2434 45.5963C57.0701 45.5963 54.8777 39.2674 54.8777 33.8284ZM164.86 22.0474C166.828 22.0474 167.87 22.554 168.869 23.0554C169.695 23.4596 170.531 23.8691 171.674 23.8691C173.606 23.8691 174.893 22.1261 174.893 20.5065C174.893 17.3329 170.866 15.1935 164.86 15.1935C153.65 15.1935 148.632 24.5543 148.632 33.8284C148.632 43.0999 153.65 52.4554 164.86 52.4554C171.316 52.4554 174.705 49.7175 174.705 47.0137C174.705 45.003 173.454 43.6538 171.607 43.6538C170.655 43.6538 169.932 44.0423 169.162 44.4544C168.13 44.9925 166.993 45.5963 164.86 45.5963C157.955 45.5963 156.116 38.199 156.116 33.8284C156.116 29.4499 157.955 22.0474 164.86 22.0474ZM31.062 7.35525C30.4908 5.83537 29.4486 3.0765 26.2114 3.0765C22.9974 3.0765 21.9835 5.69625 21.3788 7.27388C21.3556 7.32113 16.0212 21.9161 11.8294 33.3664H3.80588C1.001 33.3664 0 35.175 0 36.7238C0 38.3355 1.00615 40.0628 3.80588 40.0628H9.40018C7.9154 44.0843 6.91697 46.8169 6.91697 46.8169C6.66993 47.4311 6.38687 48.4339 6.38687 49.2555C6.38687 50.8699 7.56286 52.5 9.81963 52.5C12.187 52.5 12.9564 50.9171 13.5149 49.7726L13.6409 49.5154L16.6337 41.2125C16.6337 41.2125 16.8112 40.7059 17.0402 40.0628H26.286C29.096 40.0628 30.097 38.3355 30.097 36.7238C30.097 35.175 29.096 33.3664 26.286 33.3664H19.4437C21.2295 28.4025 24.4333 19.5037 26.214 14.5766C28.1079 19.8398 34.2503 36.9469 34.2632 36.9521C34.2632 36.9652 35.766 41.1968 35.766 41.1968C35.766 41.2125 38.7304 49.4393 38.7304 49.4393C39.1164 50.4446 39.8806 52.5 42.5774 52.5C44.8419 52.5 46.0179 50.8699 46.0179 49.2555C46.0179 48.4339 45.7323 47.439 45.4621 46.7723C45.4801 46.8169 31.062 7.35525 31.062 7.35525ZM135.151 22.0474C137.12 22.0474 138.167 22.554 139.16 23.0554C139.984 23.4596 140.823 23.8691 141.965 23.8691C143.895 23.8691 145.184 22.1261 145.184 20.5065C145.184 17.3329 141.155 15.1935 135.151 15.1935C123.944 15.1935 118.934 24.5543 118.934 33.8284C118.934 43.0999 123.944 52.4554 135.151 52.4554C141.628 52.4554 145.009 49.7175 145.009 47.0137C145.009 45.003 143.764 43.6538 141.898 43.6538C140.956 43.6538 140.218 44.0423 139.464 44.4544C138.44 44.9925 137.289 45.5963 135.151 45.5963C128.257 45.5963 126.42 38.199 126.42 33.8284C126.42 29.4499 128.257 22.0474 135.151 22.0474ZM194.172 15.1935C183.776 15.1935 178.331 24.5726 178.331 33.8284C178.331 43.0815 183.776 52.4554 194.172 52.4554C204.563 52.4554 210 43.0815 210 33.8284C210 24.5726 204.563 15.1935 194.172 15.1935ZM194.172 45.5963C188.006 45.5963 185.821 39.2674 185.821 33.8284C185.821 28.3868 188.006 22.0474 194.172 22.0474C200.358 22.0474 202.545 28.3868 202.545 33.8284C202.545 39.2674 200.358 45.5963 194.172 45.5963ZM115.028 32.9884C115.028 24.2393 109.223 15.1935 99.4906 15.1935C88.8913 15.1935 83.3356 24.5726 83.3356 33.8284C83.3356 42.4226 87.6613 52.4554 99.8123 52.4554C106.451 52.4554 114.315 48.0953 114.315 44.415C114.315 42.1496 112.782 40.9264 111.346 40.9264C110.298 40.9264 109.418 41.622 108.41 42.4226C106.729 43.7693 104.418 45.5963 99.8817 45.5963C94.7661 45.5963 91.287 42.168 90.8907 36.9941H110.96C113.736 36.9941 115.028 35.7184 115.028 32.9884ZM90.8882 30.7965C91.1918 27.7961 92.638 21.7901 99.4726 22.0474C104.931 22.0474 107.151 26.9745 107.47 30.7965H90.8882Z"
                fill="white"
              />
            </svg>
          </div>

          <AppButton
            className="signInButton generalButton"
            onClick={b2cSignIn}
            text={t('candidate:GENERAL.REGISTRATION.signin')}
          />
        </div>
      </div>
    </>
  );
};
